import { isArray, isDate, isDefined, isEmpty, isObject, isSymbol } from './guards';
import { toString } from './string';

/**
 * Type-safe version of Object.keys method that preserves the key types
 * @param obj - Object to get keys from
 * @returns Array of strongly-typed keys from the given object
 * @example
 * ```ts
 * const obj = { a: 1, b: 2 };
 * const keys = objectKeys(obj); // type: ('a' | 'b')[]
 * ```
 */
export const objectKeys = <T extends object>(obj: T): (keyof T)[] => Object.keys(obj) as (keyof T)[];

/**
 * Type-safe version of Object.values method
 * @param obj - Object to get values from
 * @returns Array of strongly-typed values from the given object
 * @example
 * ```ts
 * const obj = { a: 1, b: 'test' };
 * const values = objectValues(obj); // type: (number | string)[]
 * ```
 */
export const objectValues = <T extends object>(obj: T) => Object.values(obj);

/**
 * Type-safe version of Object.entries method that preserves key-value type relationships
 * @param obj - Object to get entries from
 * @returns Array of strongly-typed key-value pairs from the given object
 * @example
 * ```ts
 * const obj = { a: 1, b: 'test' };
 * const entries = objectEntries(obj); // type: [('a' | 'b'), (number | string)][]
 * ```
 */
export const objectEntries = <T extends object>(obj: T): [keyof T, T[keyof T]][] => Object.entries(obj) as [keyof T, T[keyof T]][];

/**
 * Deep object merge function that recursively combines properties
 * @param target - Target object to merge into
 * @param source - Source object containing properties to merge
 * @returns New object with merged properties from both objects
 * @example
 * ```ts
 * const result = merge({ a: 1, b: { x: 10 } }, { b: { y: 20 }, c: 3 });
 * // result: { a: 1, b: { x: 10, y: 20 }, c: 3 }
 * ```
 */
export const merge = <T extends object, U extends object>(target: T, source: U): T & U => {
	const output = { ...target };
	Object.entries(source).forEach(([key, value]) => {
		if (isObject(value) && key in target) {
			Object.assign(output[key], merge(target[key], value));
		} else {
			output[key] = value;
		}
	});
	return output as T & U;
};

type Many<T> = T | T[];
type PropertyPath = Many<PropertyKey>;

/**
 * Converts various path formats to a standardized array of path segments
 * @param value - Path value to convert (string, array, or symbol)
 * @returns Array of path segments as strings or symbols
 * @private
 */
function toPath(value: any): (string | symbol)[] {
	if (isSymbol(value)) {
		return [value];
	}
	if (isArray(value)) {
		return value.map((part) => (isSymbol(part) ? part : toString(part)));
	}
	return toString(value)
		.split(/[[\]\\.]/)
		.filter((key) => key !== '');
}

/**
 * Gets the value at path of object
 *
 * If the resolved value is undefined, the defaultValue is returned in its place.
 * Supports dot notation for nested properties and handles paths with dots in property names.
 *
 * @param object - The object to query
 * @param path - The path of the property to get (string with dot notation, array of path segments, or symbol)
 * @param defaultValue - The value returned for undefined resolved values
 * @returns The resolved value or defaultValue if not found
 * @example
 * ```ts
 * get({ user: { profile: { name: 'John' } } }, 'user.profile.name'); // returns 'John'
 * get({ user: { profile: { name: 'John' } } }, 'user.settings', {}); // returns {}
 * get({ 'user.profile': { name: 'John' } }, 'user.profile'); // returns { name: 'John' }
 * ```
 */
export const get = (object: any, path: PropertyPath, defaultValue?: any): any => {
	if (object === undefined) return defaultValue;

	/** check for direct key match first (for keys that include dots) */
	if (typeof path === 'string' && path in object) {
		return object[path];
	}

	const formattedPath = toPath(path);
	let result = object;
	for (let i = 0; i < formattedPath.length; i++) {
		const key = formattedPath[i]!;
		/** check for direct key match in nested objects (for keys that include dots) */
		if (i < formattedPath.length - 1) {
			const nestedKey = formattedPath.slice(i).join('.');
			if (nestedKey in result) {
				return result[nestedKey];
			}
		}
		result = result[key];
		if (result === undefined) {
			return defaultValue;
		}
	}
	return result;
};

/**
 * Sets the value at path of object
 *
 * If a portion of path doesn't exist, it's created. Arrays are created for missing
 * index properties while objects are created for all other missing properties.
 *
 * @param object - The object to modify
 * @param path - The path of the property to set (string with dot notation, array of path segments, or symbol)
 * @param value - The value to set
 * @returns The modified object
 * @example
 * ```ts
 * const obj = { user: { profile: {} } };
 * set(obj, 'user.profile.name', 'John');
 * // obj is now { user: { profile: { name: 'John' } } }
 *
 * set({}, 'items[0]', 'value');
 * // returns { items: ['value'] }
 * ```
 */
export const set = (object: any, path: PropertyPath, value: any): any => {
	if (object === null || object === undefined) {
		return object;
	}

	const formattedPath = toPath(path);
	if (formattedPath.length === 0) {
		return object;
	}

	let current = object;
	const lastIndex = formattedPath.length - 1;

	for (let i = 0; i < lastIndex; i++) {
		const key = formattedPath[i]!;
		const nextKey = formattedPath[i + 1]!;

		const isNextKeyArrayIndex = !Number.isNaN(Number(nextKey));

		if (current[key] === undefined || current[key] === null) {
			current[key] = isNextKeyArrayIndex ? [] : {};
		}

		current = current[key];
	}

	current[formattedPath[lastIndex]!] = value;
	return object;
};

/**
 * Cleans object by removing falsy values
 *
 * Removes `undefined`, `null`, empty objects or empty strings from an object.
 * Recursively cleans nested objects and provides options for string trimming.
 *
 * @param inputObject - Object to clean
 * @param options - Configuration options
 * @param options.trimString - Whether to trim string values (default: true)
 * @param options.allowEmptyString - Whether to keep empty strings (default: false)
 * @returns Cleaned object without falsy values
 * @example
 * ```ts
 * cleanObject({
 *   name: 'John',
 *   email: '',
 *   details: { age: null, address: undefined },
 *   notes: '  important  '
 * });
 * // returns { name: 'John', notes: 'important' }
 *
 * cleanObject({ email: '' }, { allowEmptyString: true });
 * // returns { email: '' }
 * ```
 */
export const cleanObject = <T extends Record<PropertyKey, any>>(
	inputObject: T,
	options: { trimString?: boolean; allowEmptyString?: boolean } = { trimString: true, allowEmptyString: false },
): T => {
	/** process entries */
	const entries = Object.entries(inputObject)
		.filter(([_, value]) => {
			if (options.allowEmptyString) return isDefined(value);
			return !isEmpty(value);
		})
		.map(([k, value]) => {
			let processedValue = value;

			if (Array.isArray(value)) {
				/** preserve arrays as is */
				processedValue = value;
			} else if (isObject(value) && !isDate(value)) {
				/** recursively clean nested objects */
				processedValue = cleanObject(value, options);

				/** filter out empty objects after cleaning */
				if (isObject(processedValue) && Object.keys(processedValue).length === 0) {
					return null;
				}
			} else if (options.trimString && typeof value === 'string') {
				/** trim string values if option is enabled */
				processedValue = value.trim();
			}

			return [k, processedValue];
		})
		.filter((entry) => entry !== null);

	return Object.fromEntries(entries) as T;
};

/**
 * Picks specified properties from provided object
 *
 * Creates a new object with only the specified properties from the original.
 *
 * @param base - Source object to pick properties from
 * @param keys - Property keys to include in the resulting object
 * @returns New object with only the specified properties
 * @example
 * ```ts
 * const user = { id: 1, name: 'John', email: 'john@example.com', role: 'admin' };
 * pickFrom(user, 'id', 'name');
 * // returns { id: 1, name: 'John' }
 * ```
 */
export const pickFrom = <T extends object, K extends keyof T>(base: T, ...keys: K[]): Pick<T, K> => {
	const entries = keys.map((key) => [key, base[key]]);
	return Object.fromEntries(entries);
};

/**
 * Omits specified properties from provided object
 *
 * Creates a new object excluding the specified properties from the original.
 *
 * @param base - Source object to omit properties from
 * @param keys - Property keys to exclude from the resulting object
 * @returns New object without the specified properties
 * @example
 * ```ts
 * const user = { id: 1, name: 'John', password: 'secret', token: '123456' };
 * omitFrom(user, 'password', 'token');
 * // returns { id: 1, name: 'John' }
 * ```
 */
export const omitFrom = <T extends object, K extends keyof T>(base: T, ...keys: K[]): Omit<T, K> => {
	const entries = (Object.keys(base) as K[]).filter((k) => !keys.includes(k)).map((key) => [key, base[key]]);
	return Object.fromEntries(entries);
};

/**
 * Removes a prefix from all keys in an object type
 *
 * Creates a new type with keys that had the specified prefix removed.
 *
 * @template TObj - The original object type
 * @template TPrefix - The prefix to remove from keys
 * @example
 * ```ts
 * type Unprefixed = UnprefixKeys<{
 *   'prefix.key1': string,
 *   'prefix.key2': string,
 *   'prefix.nested.key3': string,
 *   'other.key': boolean
 * }, 'prefix'>;
 * // {
 * //   key1: string,
 * //   key2: string,
 * //   'nested.key3': string
 * // } (note 'other.key' is excluded)
 * ```
 */
export type UnprefixKeys<TObj extends Record<string, any>, TPrefix extends string> = {
	[K in keyof TObj as K extends `${TPrefix}.${infer Rest}` ? Rest : never]: TObj[K];
};

/**
 * Removes a prefix from all keys in an object
 *
 * Creates a new object with keys that had the specified prefix removed.
 * Keys that don't start with the prefix are excluded from the result.
 *
 * @param obj - The object to process
 * @param prefix - The prefix to remove from keys
 * @returns A new object with unprefixed keys
 * @example
 * ```ts
 * const obj = {
 *   'user.name': 'John',
 *   'user.email': 'john@example.com',
 *   'settings.theme': 'dark'
 * };
 * unprefixKeys(obj, 'user');
 * // returns { name: 'John', email: 'john@example.com' }
 * ```
 */
export function unprefixKeys<TObj extends Record<string, any>, TPrefix extends string>(obj: TObj, prefix: TPrefix): UnprefixKeys<TObj, TPrefix> {
	const result: Record<string, any> = {};
	Object.entries(obj).forEach(([key, value]) => {
		if (key.startsWith(`${prefix}.`)) {
			const unprefixedKey = key.slice(prefix.length + 1);
			result[unprefixedKey] = value;
		}
	});
	return result as UnprefixKeys<TObj, TPrefix>;
}
